import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { displayToastError } from '@/helpers';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { AuthenticatedRoutes } from '@/router/routes';

import { createBills } from '../../api';
import { BILLS_FILTER_KEY_BILLING_PERIOD } from '../../constants/bills-filter-keys';
import { BILLS_QUERY_KEY } from '../../hooks';
import { mapGenerateBillsToRequest } from '../../mappers';
import { GenerateBillListItem } from '../../types';

interface GenerateBillsModalProps {
  isOpen: boolean;
  onClose: () => void;
  billingPeriodLabel: string;
  billingPeriod: string;
  billMembers: GenerateBillListItem[];
  onSuccess?: () => void;
}

// Return to the list with only the generated period in the URL. Since the persisted-filters
// hook skips restoring localStorage when an owned key is present, this resets every other
// filter so the newly-created bills (which fall in this period) are visible.
const buildBillsListUrl = (billingPeriod: string): string => {
  if (!billingPeriod) return AuthenticatedRoutes.Bills;
  const params = new URLSearchParams({ [BILLS_FILTER_KEY_BILLING_PERIOD]: billingPeriod });
  return `${AuthenticatedRoutes.Bills}?${params.toString()}`;
};

export const GenerateBillsModal = ({
  onClose,
  isOpen,
  billingPeriodLabel,
  billingPeriod,
  billMembers,
  onSuccess,
}: GenerateBillsModalProps) => {
  const { t } = useTranslation('authenticated.bills', { keyPrefix: 'modals.generateBills' });
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { getErrorMessage } = useApiError();

  const { mutate, isPending } = useMutation({
    mutationFn: createBills,
    onError: (error: unknown) => {
      displayToastError(getErrorMessage(error, true));
    },
    onSuccess: () => {
      // Reset form before navigation to prevent unsaved changes guard from triggering
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      navigate(buildBillsListUrl(billingPeriod));
      onClose();
    },
  });

  const handleConfirm = () => {
    // Map the bill members to API request format
    const billsRequest = mapGenerateBillsToRequest(billMembers, billingPeriod);

    mutate(billsRequest);
  };

  return (
    <ConfirmationModal
      title={t('title')}
      hideCloseIcon
      message={
        <div>
          <Trans
            t={t}
            i18nKey="description"
            values={{ billingPeriod: billingPeriodLabel }}
            count={billingPeriodLabel ? 1 : 0}
          />
          <Alert variant={'warning'} className="mt-2">
            <AlertTriangle className="h-5 w-5" />
            <AlertDescription>{t('warning')}</AlertDescription>
          </Alert>
        </div>
      }
      confirmText={t('submit')}
      confirmVariant="default"
      open={isOpen}
      isLoading={isPending}
      onClose={onClose}
      onConfirm={handleConfirm}
    />
  );
};
